import clsx from "clsx";
import { forwardRef, useId, type InputHTMLAttributes, type TextareaHTMLAttributes } from "react";
import { twMerge } from "tailwind-merge";

type TextFieldProps = (InputHTMLAttributes<HTMLInputElement> | TextareaHTMLAttributes<HTMLTextAreaElement>) & {
    errorText?: string;
    variant?: "transparent" | "outlined";
    size?: "lg" | "md";
    endAdornment?: React.ReactNode;
    error?: boolean;
    label?: string;
    labelClassName?: string;
    multiline?: boolean;
    rows?: number;
    type?: string;
};

const TextFieldComponent = forwardRef<HTMLInputElement | HTMLTextAreaElement, TextFieldProps>(
    (
        {
            errorText,
            error,
            className,
            type = "text",
            variant = "transparent",
            size = "lg",
            endAdornment,
            label,
            labelClassName,
            multiline,
            rows,
            ...props
        },
        ref,
    ) => {
        const id = useId();
        const baseClassName =
            "w-full text-primary placeholder:text-muted focus:outline-none focus:ring-0 focus:ring-primary/40 focus:border-primary disabled:cursor-not-allowed disabled:bg-black/10 disabled:text-muted";

        const variantClassName = clsx({
            "border-transparent bg-transparent": variant === "transparent",
            "border-transparent": variant === "outlined",
        });

        const sizeClassName = clsx({
            "text-[22px] leading-[30px]": size === "lg",
            "text-[15px] leading-[22.5px]": size === "md",
        });

        const inputClassName = twMerge(
            clsx(
                baseClassName,
                variantClassName,
                sizeClassName,
                (errorText || error) && "border-red-500/70 focus:border-red-500",
            ),
            className,
        );

        const inputProps = props as InputHTMLAttributes<HTMLInputElement>;
        const textareaProps = props as TextareaHTMLAttributes<HTMLTextAreaElement>;

        return (
            <div className="flex w-full flex-col gap-1">
                {label ? (
                    <label htmlFor={id} className={twMerge("text-[12px] text-muted", labelClassName)}>
                        {label}
                    </label>
                ) : null}
                {endAdornment ? (
                    <div className=" relative w-full">
                        {multiline ? (
                            <textarea
                                id={id}
                                ref={ref as React.Ref<HTMLTextAreaElement>}
                                rows={rows}
                                className={inputClassName}
                                {...textareaProps}
                            />
                        ) : (
                            <input
                                id={id}
                                ref={ref as React.Ref<HTMLInputElement>}
                                type={type}
                                className={inputClassName}
                                {...inputProps}
                            />
                        )}
                        <div className="absolute right-0 top-1/2 -translate-y-1/2">{endAdornment}</div>
                    </div>
                ) : multiline ? (
                    <textarea
                        id={id}
                        ref={ref as React.Ref<HTMLTextAreaElement>}
                        rows={rows}
                        className={inputClassName}
                        {...textareaProps}
                    />
                ) : (
                    <input
                        id={id}
                        ref={ref as React.Ref<HTMLInputElement>}
                        type={type}
                        className={inputClassName}
                        {...inputProps}
                    />
                )}
                {errorText ? <span className="text-[12px] text-red-500">{errorText}</span> : null}
            </div>
        );
    },
);

export const TextField = TextFieldComponent;

TextField.displayName = "TextField";
